// frontend/app/components/lessonPlans/LessonInsightsPanel.js
//
// Teacher-facing rollup of GET /api/lesson-plans/{id}/class-insights, read as a
// page rather than a panel: the one question a teacher opens this with — what
// should I reteach — answered in a sentence at the top, with the evidence for
// that sentence laid out underneath it.
//
// Props-only, like LessonPlanLauncher: the workspace owns the fetch, this file
// only owns the reading of it.
//
// Four things here are deliberate and easy to mistake for complexity worth
// removing.
//
// The bars are not "just a percentage". Lesson-plan checks get answered a
// handful at a time, so a concept can read 100% off two answers by one student.
// Every bar therefore carries its sample size, a Wilson interval showing how far
// the truth could sit from the drawn value, and a faded fill while the sample is
// thin — a teacher should never pull a group aside on the strength of noise.
//
// A concept nobody has answered is drawn as a dashed empty track, never as a 0%
// bar, while a measured zero keeps a 4px solid stub. "Not asked yet" and "asked,
// and everybody missed it" call for opposite responses, so they must never share
// a picture.
//
// "N written answers to read" is a link, not a caption. It said so for a while
// with nowhere to go — a tester read the sentence and went looking — so it now
// opens the TeachSense written-answers section filtered to that concept. It is
// only pressable where that section can actually be reached; a control that
// looks live and does nothing is worse than the caption it replaced.
//
// And no student's email is ever painted. These screens go on a projector, so
// the address lives only in accessibilityLabel and the visible name comes from
// the payload — or, failing that, from a humanized local part shown a step down
// in contrast, because a guess should not read like a fact.

import React, { useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { useFeatureFlags } from "../../features";
import { getLessonColors, tint } from "./lessonPlanTheme";
import { ActionButton, Pill } from "./blocks/LessonBlockShell";
import TeachSenseTabs, { INSIGHTS_TAB, TEACHSENSE_TAB } from "./teachsense/TeachSenseTabs";
import TeachSenseView from "./teachsense/TeachSenseView";
import { useTeachSense } from "./teachsense/useTeachSense";
import { countLabel, fill } from "./teachsense/teachsenseText";
import { displayName, initialsOf } from "./teachsense/studentIdentity";

// These two moved down to teachsense/studentIdentity.js once the TeachSense
// sections needed the same two helpers — importing them back out of the panel
// they compose into would have been a cycle. Still exported from here, where
// every existing caller looks for them.
export { displayName, initialsOf };

// Mirrors MASTERY_THRESHOLD / NEEDS_SUPPORT_THRESHOLD in backend/lesson_plans_api.py,
// so a bar's colour means the same thing as the list a student landed in.
const MASTERY_PCT = 80;
const NEEDS_SUPPORT_PCT = 60;

// Below this many answers a bar is drawn as provisional rather than as fact.
const CONFIDENT_SAMPLE = 10;

const Z = 1.96; // 95% normal quantile

const MAX_CONTENT_WIDTH = 1240;
const WIDE_MIN = 1200; // >= this: table concept rows + side-by-side student columns
const NARROW_MAX = 720; // <  this: everything stacks
const TRACK_MAX = 360; // a mastery bar is NEVER wider than this, at any viewport
const BAR_COL = 300; // fixed bar column width in the wide table
const MAX_STUDENT_ROWS = 10; // then "+N more students"
const MAX_WEAK_PILLS = 3; // then "+N"

// Fixed accents for the verdict card. Same hexes the bands use, so the card and
// the bar it is talking about are never two different reds.
const TONE_HEX = {
  danger: "#ef4444",
  warning: "#f59e0b",
  success: "#10b981",
  info: "#3b82f6",
};

const SUPPORT_HEX = "#ef4444";
const READY_HEX = "#10b981";

/**
 * Wilson score interval, in percent. Preferred over the textbook normal
 * interval because it stays inside 0–100 and stays sane at n = 1, the regime
 * these bars actually live in. It treats each answer as independent, so it is
 * the optimistic end of the uncertainty — five answers from one student are
 * less information than five from five, which the student count alongside is
 * what tells.
 */
export const wilsonInterval = (correct, answered) => {
  if (!answered) return null;
  const p = Math.min(1, Math.max(0, correct / answered));
  const z2 = Z * Z;
  const denom = 1 + z2 / answered;
  const centre = (p + z2 / (2 * answered)) / denom;
  const margin =
    (Z * Math.sqrt((p * (1 - p)) / answered + z2 / (4 * answered * answered))) /
    denom;
  return {
    lo: Math.max(0, (centre - margin) * 100),
    hi: Math.min(100, (centre + margin) * 100),
  };
};

export const bandFor = (pct, colors) => {
  if (pct === null || pct === undefined) {
    return { key: "unanswered", accent: colors.textMuted, hex: "#64748b" };
  }
  if (pct >= MASTERY_PCT) {
    return { key: "strong", accent: colors.success, hex: "#10b981" };
  }
  if (pct >= NEEDS_SUPPORT_PCT) {
    return { key: "mixed", accent: colors.warning, hex: "#f59e0b" };
  }
  return { key: "weak", accent: colors.danger, hex: "#ef4444" };
};

const BAND_LABEL = { strong: "Strong", mixed: "Mixed", weak: "Needs work" };

/**
 * Weakest first, so the concepts the verdict just named are the first rows under
 * it. Concepts with no graded data cannot be ranked, so they fall to the bottom
 * in two groups: work that is waiting to be read, then work nobody has touched.
 */
const orderConcepts = (concepts) => {
  const graded = [];
  const toRead = [];
  const untouched = [];
  concepts.forEach((c) => {
    if (Number(c.answered) > 0 && c.mastery_pct !== null && c.mastery_pct !== undefined) {
      graded.push(c);
    } else if (Number(c.ungraded) > 0) {
      toRead.push(c);
    } else {
      untouched.push(c);
    }
  });
  return [
    ...graded.sort((a, b) => Number(a.mastery_pct) - Number(b.mastery_pct)),
    ...toRead,
    ...untouched,
  ];
};

/**
 * The whole page in one sentence. A teacher who reads nothing else below this
 * card still leaves knowing what to do next, which is the only reason the rest
 * of the page is allowed to be as long as it is.
 */
const buildVerdict = ({ concepts, needsSupport, readyToAdvance, started, colors, t }) => {
  const graded = concepts.filter(
    (c) => Number(c.answered) > 0 && c.mastery_pct !== null && c.mastery_pct !== undefined
  );
  const byMastery = [...graded].sort((a, b) => Number(a.mastery_pct) - Number(b.mastery_pct));
  const belowSupport = byMastery.filter((c) => Number(c.mastery_pct) < NEEDS_SUPPORT_PCT);
  const belowMastery = byMastery.filter((c) => Number(c.mastery_pct) < MASTERY_PCT);
  const totalUngraded = concepts.reduce((sum, c) => sum + (Number(c.ungraded) || 0), 0);

  const supportClause =
    needsSupport.length > 0 && started > 0
      ? ` ${fill(t("{n} of {total} students are below the {pct}% support line."), {
          n: needsSupport.length,
          total: started,
          pct: NEEDS_SUPPORT_PCT,
        })}`
      : "";
  const readyClause =
    readyToAdvance.length > 0
      ? ` ${fill(t("{n} students are ready to move on."), { n: readyToAdvance.length })}`
      : "";

  // Two names is the most a teacher can hold while deciding; the rest are a count.
  const naming = (entries) => {
    const head = entries
      .slice(0, 2)
      .map((c) => {
        const thin = (Number(c.answered) || 0) < CONFIDENT_SAMPLE;
        const pct = Math.round(Number(c.mastery_pct));
        return `${c.label} (${pct}%${thin ? `, ${t("thin sample")}` : ""})`;
      })
      .join(` ${t("and")} `);
    const rest = entries.length - 2;
    return rest > 0 ? `${head} ${fill(t("+{n} more"), { n: rest })}` : head;
  };

  const verdictFor = (entries, tone, label) => ({
    tone,
    label,
    body: `${naming(entries)}.${supportClause}`,
    band: bandFor(Number(entries[0].mastery_pct), colors),
  });

  if (belowSupport.length) {
    return verdictFor(belowSupport, "danger", t("Reteach first"));
  }
  if (belowMastery.length) {
    return verdictFor(belowMastery, "warning", t("Worth a second pass"));
  }
  if (graded.length) {
    return {
      tone: "success",
      label: t("Class is ready"),
      body: `${fill(t("Every concept is at or above the {pct}% mastery bar."), {
        pct: MASTERY_PCT,
      })}${readyClause}`,
      band: null,
    };
  }
  if (totalUngraded > 0) {
    return {
      tone: "info",
      label: t("Nothing graded yet"),
      body: fill(t("{n} written answers are waiting to be read."), { n: totalUngraded }),
      band: null,
    };
  }
  return {
    tone: "info",
    label: t("Nothing graded yet"),
    body: t("No checks have been answered yet."),
    band: null,
  };
};

const VerdictCard = ({ verdict, colors, isDarkMode }) => {
  const accent = verdict.band ? verdict.band.accent : colors[verdict.tone];
  const hex = verdict.band ? verdict.band.hex : TONE_HEX[verdict.tone];

  return (
    <View
      style={[
        styles.verdict,
        {
          backgroundColor: tint(hex, isDarkMode, 1.2),
          borderColor: tint(hex, isDarkMode, 2.6),
          borderLeftColor: accent,
        },
      ]}
    >
      <Text style={[styles.verdictLabel, { color: accent }]}>{verdict.label}</Text>
      <Text style={[styles.verdictBody, { color: colors.textPrimary }]}>{verdict.body}</Text>
    </View>
  );
};

const Stat = ({ colors, icon, accent, value, caption }) => (
  <View style={styles.stat}>
    <MaterialCommunityIcons name={icon} size={14} color={accent} />
    <Text style={[styles.statValue, { color: colors.textPrimary }]}>{value}</Text>
    <Text style={[styles.statCaption, { color: colors.textMuted }]}>{caption}</Text>
  </View>
);

/**
 * One concept's mastery, drawn as: uncertainty band (how wide the truth could
 * be), solid fill (the point estimate), and a tick at the estimate so it stays
 * findable inside a wide band.
 *
 * Wide lays the same parts out as a table row so five concepts scan as five
 * lines; medium and narrow stack them. The track is capped either way — extra
 * viewport width belongs to the evidence beside the bar, never to the bar.
 */
const ConceptRow = ({
  concept,
  colors,
  isDarkMode,
  isWide,
  isNarrow,
  divided,
  // Supplied only when the written answers are reachable. Null keeps the
  // ungraded line the plain caption it has always been.
  onReadAnswers = null,
  t,
}) => {
  const answered = Number(concept.answered) || 0;
  const correct = Number(concept.correct) || 0;
  const students = Number(concept.student_count) || 0;
  const hasData = answered > 0 && concept.mastery_pct !== null && concept.mastery_pct !== undefined;
  const pct = hasData ? Number(concept.mastery_pct) : null;

  const band = bandFor(pct, colors);
  const interval = hasData ? wilsonInterval(correct, answered) : null;
  const thin = hasData && answered < CONFIDENT_SAMPLE;

  // Written answers carry no right/wrong, so they are not evidence of mastery
  // and never reach the bar. They are still work the teacher has to read, so
  // they are counted alongside it rather than dropped.
  const ungraded = Number(concept.ungraded) || 0;
  const toReview = ungraded
    ? `${ungraded} ${t(ungraded === 1 ? "written answer to read" : "written answers to read")}`
    : "";

  const sampleText = `${countLabel(answered, "answer", "answers", t)} · ${countLabel(
    students,
    "student",
    "students",
    t
  )}`;

  // A concept checked only by a written prompt is not unanswered — it is
  // ungraded, and saying "not yet answered" over a stack of exit tickets
  // would send the teacher looking for work that is already in.
  const emptyLabel = ungraded ? t("To read") : t("Not yet answered");

  // At wide the percentage column is 56px — room for "100%" and nothing else.
  // "Not yet answered" is about 90px at 11px type, so leaving it there wrapped
  // the cell to three lines and pushed the row past the 56px rhythm the whole
  // wide table exists for. It moves to the 210px evidence column instead, and
  // the number column takes a dash.
  const emptyInEvidence = isWide && !hasData;

  const evidence = [
    emptyInEvidence ? { key: "empty", text: emptyLabel, color: colors.textMuted } : null,
    {
      key: "sample",
      // Redundant beside the label above, so the wide column drops it.
      text: hasData ? sampleText : emptyInEvidence ? "" : t("No answers recorded yet"),
      color: colors.textMuted,
    },
    ungraded
      ? {
          key: "ungraded",
          text: toReview,
          color: colors.info,
          // The only line on this row that names work rather than measuring
          // it, so it is the only line that has anywhere to go.
          onPress: onReadAnswers ? () => onReadAnswers(concept) : null,
        }
      : null,
    thin ? { key: "thin", text: t("Too few answers to trust"), color: colors.warning } : null,
  ]
    .filter(Boolean)
    .filter((line) => line.text);

  const accessibilityLabel = [
    hasData
      ? `${concept.label}: ${Math.round(pct)}% ${t("correct")}, ${sampleText}`
      : `${concept.label}: ${emptyLabel}`,
    ungraded ? `, ${toReview}` : "",
    thin ? `, ${fill(t("provisional — only {n} answers"), { n: answered })}` : "",
  ].join("");

  const labelNode = (
    <Text style={[styles.conceptLabel, { color: colors.textPrimary }]} numberOfLines={2}>
      {concept.label}
    </Text>
  );

  const pctNode = hasData ? (
    <Text
      style={[
        styles.conceptPct,
        isWide ? styles.alignRight : styles.pctInline,
        { color: band.accent },
      ]}
    >
      {`${Math.round(pct)}%`}
    </Text>
  ) : emptyInEvidence ? (
    <Text style={[styles.conceptPct, styles.alignRight, { color: colors.textMuted }]}>
      {"\u2014"}
    </Text>
  ) : (
    <Text
      style={[
        styles.conceptUnanswered,
        isWide ? styles.alignRight : styles.pctInline,
        { color: colors.textMuted },
      ]}
    >
      {emptyLabel}
    </Text>
  );

  const trackNode = (
    <View
      testID={`insights-track-${concept.concept_key}`}
      style={[
        styles.track,
        { backgroundColor: colors.subtleBg, borderColor: colors.border },
        hasData ? null : styles.trackEmpty,
      ]}
    >
      {interval ? (
        <View
          testID={`insights-band-${concept.concept_key}`}
          style={[
            styles.interval,
            {
              left: `${interval.lo}%`,
              width: `${Math.max(interval.hi - interval.lo, 0.5)}%`,
              backgroundColor: tint(band.hex, isDarkMode, 2.4),
            },
          ]}
        />
      ) : null}
      {hasData ? (
        <>
          <View
            testID={`insights-fill-${concept.concept_key}`}
            style={[
              styles.fill,
              {
                // A measured zero keeps a visible stub, so it can never be read
                // as the same thing as the dashed track of an unasked concept.
                width: pct > 0 ? `${pct}%` : 4,
                backgroundColor: band.accent,
                // A thin sample is drawn washed-out so it cannot be mistaken
                // for a settled result at a glance.
                opacity: thin ? 0.45 : 1,
              },
            ]}
          />
          <View style={[styles.marker, { left: `${pct}%`, backgroundColor: band.accent }]} />
        </>
      ) : null}
    </View>
  );

  // A pressable line keeps the same 11px type and the same 15px line height as
  // the captions around it, so turning one into a link does not change the
  // height of a wide row or the 210px the evidence column is allowed.
  const evidenceNode = evidence.map((line, index) => {
    const stacked = index > 0 ? styles.evidenceStacked : null;
    if (!line.onPress) {
      return (
        <Text key={line.key} style={[styles.evidence, stacked, { color: line.color }]}>
          {line.text}
        </Text>
      );
    }
    return (
      <TouchableOpacity
        key={line.key}
        testID={`insights-read-${concept.concept_key}`}
        accessibilityRole="button"
        accessibilityLabel={`${line.text}, ${t("open in TeachSense")}`}
        onPress={line.onPress}
        style={[styles.evidenceLink, stacked]}
      >
        <Text style={[styles.evidence, styles.evidenceLinkText, { color: line.color }]}>
          {line.text}
        </Text>
        <MaterialCommunityIcons name="chevron-right" size={12} color={line.color} />
      </TouchableOpacity>
    );
  });

  // Exactly one pill, so every wide row is the same height.
  const statusNode = hasData ? (
    <Pill
      colors={colors}
      label={t(BAND_LABEL[band.key])}
      accent={band.accent}
      background={tint(band.hex, isDarkMode, 1.4)}
    />
  ) : null;

  return (
    <View
      testID={`insights-concept-${concept.concept_key}`}
      accessibilityLabel={accessibilityLabel}
      style={[
        styles.conceptRow,
        isWide ? styles.conceptRowWide : null,
        divided ? { borderTopWidth: 1, borderTopColor: colors.border } : null,
      ]}
    >
      {isWide ? (
        <>
          <View style={styles.colLabel}>{labelNode}</View>
          <View style={styles.colBar}>{trackNode}</View>
          <View style={styles.colPct}>{pctNode}</View>
          <View style={styles.colEvidence}>{evidenceNode}</View>
          <View style={styles.colStatus}>{statusNode}</View>
        </>
      ) : (
        <>
          <View style={styles.conceptTop}>
            {labelNode}
            {pctNode}
          </View>
          {trackNode}
          <View style={isNarrow ? styles.conceptMetaNarrow : styles.conceptMeta}>
            <View style={isNarrow ? null : styles.evidenceCol}>{evidenceNode}</View>
            {statusNode}
          </View>
        </>
      )}
    </View>
  );
};

const StudentRow = ({ entry, colors, isDarkMode, accent, hex, showWeak, t }) => {
  const name = displayName(entry);
  // A humanized email local part is a guess, so it reads one step back in
  // contrast — a real name gets full weight, an inferred one does not.
  const inferred = !String(entry?.student_name || "").trim();
  // "Unknown student" is the absence of an identity, not an identity, so it
  // gets a question mark rather than the initials of the placeholder wording.
  const anonymous = inferred && !String(entry?.student_email || "").trim();
  const weak = showWeak && Array.isArray(entry.weak_concepts) ? entry.weak_concepts : [];
  const shownWeak = weak.slice(0, MAX_WEAK_PILLS);
  const hiddenWeak = weak.length - shownWeak.length;
  const score = Math.round(Number(entry.score_pct) || 0);

  return (
    <View
      style={[styles.studentRow, { borderTopColor: colors.border }]}
      accessibilityLabel={`${name}, ${entry.student_email}, ${score}%, ${entry.correct} ${t(
        "of"
      )} ${entry.answered} ${t("correct")}`}
    >
      <View style={[styles.avatar, { backgroundColor: tint(hex, isDarkMode, 1.6) }]}>
        <Text style={[styles.avatarText, { color: accent }]}>
          {anonymous ? "?" : initialsOf(name)}
        </Text>
      </View>
      <View style={styles.studentBody}>
        <View style={styles.studentHead}>
          <Text
            style={[
              styles.studentName,
              { color: inferred ? colors.textSecondary : colors.textPrimary },
            ]}
            numberOfLines={1}
          >
            {name}
          </Text>
          <Text style={[styles.studentScore, { color: accent }]}>{`${score}%`}</Text>
        </View>
        <Text style={[styles.studentSample, { color: colors.textMuted }]}>
          {`${entry.correct}/${entry.answered} ${t("correct")}`}
        </Text>
        {shownWeak.length > 0 ? (
          <View style={styles.weakRow}>
            {shownWeak.map((label) => (
              <Pill
                key={label}
                colors={colors}
                label={label}
                accent={accent}
                background={tint(hex, isDarkMode, 1.4)}
              />
            ))}
            {hiddenWeak > 0 ? (
              <Pill
                colors={colors}
                label={`+${hiddenWeak}`}
                accent={colors.textSecondary}
                background="transparent"
              />
            ) : null}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const StudentList = ({
  title,
  icon,
  entries,
  emptyText,
  accent,
  hex,
  showWeak,
  cardStyle,
  colors,
  isDarkMode,
  t,
}) => {
  const shown = entries.slice(0, MAX_STUDENT_ROWS);
  const overflow = entries.length - shown.length;

  return (
    <View style={cardStyle}>
      <View style={styles.listHeader}>
        <MaterialCommunityIcons name={icon} size={18} color={accent} />
        <Text
          accessibilityRole="header"
          style={[styles.listTitle, { color: colors.textPrimary }]}
        >
          {t(title)}
        </Text>
        <View style={[styles.listCount, { backgroundColor: tint(hex, isDarkMode, 1.6) }]}>
          <Text style={[styles.listCountText, { color: accent }]}>{entries.length}</Text>
        </View>
      </View>
      {entries.length === 0 ? (
        <Text style={[styles.emptyText, { color: colors.textMuted }]}>{t(emptyText)}</Text>
      ) : (
        shown.map((entry) => (
          <StudentRow
            key={entry.student_email}
            entry={entry}
            colors={colors}
            isDarkMode={isDarkMode}
            accent={accent}
            hex={hex}
            showWeak={showWeak}
            t={t}
          />
        ))
      )}
      {overflow > 0 ? (
        <Text style={[styles.moreText, { color: colors.textMuted }]}>
          {fill(t("+{n} more students"), { n: overflow })}
        </Text>
      ) : null}
    </View>
  );
};

const LessonInsightsPanel = ({
  insights = null,
  loading = false,
  error = null,
  isDarkMode = false,
  onClose,
  onRetry,
  style,
  t,
  // TeachSense (Saras Learning Loop). Everything below is optional: with none
  // of it supplied this is exactly the class-insights page it has always been.
  //
  // `fetchWithAuth` is what turns the second tab on — the panel loads
  // TeachSense itself, lazily, because the tab is the only thing that knows
  // when that data is wanted and firing it on mount would defeat the point.
  // Passing `teachsense` hands it a payload directly instead and no fetch
  // happens at all, which is how the tests and any future workspace-owned
  // fetch drive it.
  fetchWithAuth = null,
  lessonPlanId = null,
  classId = null,
  teachsense = null,
  teachsenseLoading = false,
  teachsenseError = null,
  onRetryTeachSense = null,
}) => {
  const translate = t || ((value) => value);
  const colors = getLessonColors(isDarkMode);

  // Read the same way its sibling surfaces read `lesson_plan`. The flag gates
  // the TeachSense entry only — the class-insights page below is untouched by
  // it, so a lesson on the shipped five-phase demo still opens as it did.
  const flags = useFeatureFlags();
  const learningLoopEnabled = flags?.learning_loop === true;

  // The panel shares the content area with the Saras rail, so window width
  // overstates how much room it actually has. Measure the container instead and
  // fall back to the window only for the very first frame.
  const { width: windowWidth } = useWindowDimensions();
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const width = measuredWidth || windowWidth;
  const isWide = width >= WIDE_MIN;
  const isNarrow = width < NARROW_MAX;

  // The class-insights payload already carries the two ids TeachSense needs, so
  // a caller only has to hand over fetchWithAuth to turn the tab on.
  const teachSenseLessonId = lessonPlanId ?? insights?.lesson_plan_id ?? null;
  const teachSenseClassId = classId ?? insights?.class_id ?? null;
  const injected = teachsense !== null && teachsense !== undefined;

  const [tab, setTab] = useState(INSIGHTS_TAB);
  const showTeachSense =
    learningLoopEnabled && (injected || typeof fetchWithAuth === "function");
  // A flag flipped off under a teacher who is already on the second tab must
  // put them back on the first one, not on a tab that no longer exists.
  const activeTab = showTeachSense ? tab : INSIGHTS_TAB;

  // The concept a teacher pressed "N written answers to read" on, carried
  // across the tab switch so TeachSense opens on that concept's answers rather
  // than on the whole class. `{ key, label }` because the label lives on the
  // class-insights payload and the TeachSense side only ever sees the key.
  const [focus, setFocus] = useState(null);

  // Only wired up where the destination exists. With the flag off or no way to
  // load TeachSense this stays null and the line stays a caption.
  const openWrittenAnswers = showTeachSense
    ? (concept) => {
        setFocus({ key: concept.concept_key, label: concept.label });
        setTab(TEACHSENSE_TAB);
      }
    : null;

  // Pressing the tab itself means "show me everything", so it clears whatever
  // concept a previous press had narrowed the section to.
  const changeTab = (key) => {
    setFocus(null);
    setTab(key);
  };

  const loadedTeachSense = useTeachSense({
    fetchWithAuth: injected ? null : fetchWithAuth,
    lessonPlanId: teachSenseLessonId,
    classId: teachSenseClassId,
    enabled: showTeachSense && activeTab === TEACHSENSE_TAB,
  });

  const list = (value) => (Array.isArray(value) ? value : []);
  const concepts = list(insights?.concepts);
  const needsSupport = list(insights?.needs_support);
  const readyToAdvance = list(insights?.ready_to_advance);
  const totalAnswers = concepts.reduce((sum, c) => sum + (Number(c.answered) || 0), 0);
  const started = Number(insights?.students_started) || 0;
  const roster = Number(insights?.student_count) || 0;
  const completed = Number(insights?.students_completed) || 0;

  // The payload has no "students who answered" field, so take the tightest
  // floor available: the busiest concept, or the students named in either
  // list. Both under-report if students answered disjoint concepts.
  const answeredStudents = Math.max(
    0,
    new Set([...needsSupport, ...readyToAdvance].map((e) => e.student_email)).size,
    ...concepts.map((c) => Number(c.student_count) || 0)
  );

  const cardStyle = [
    styles.card,
    isNarrow ? styles.cardNarrow : null,
    { backgroundColor: colors.panelBg, borderColor: colors.border },
  ];

  // Header and participation strip frame every state, so a failed fetch never
  // collapses the page to a bare sentence floating on the background.
  const page = (children) => (
    <View
      style={[styles.root, style]}
      onLayout={(event) => setMeasuredWidth(event.nativeEvent.layout.width)}
    >
      <View
        testID="insights-page"
        style={[
          styles.inner,
          {
            paddingHorizontal: isNarrow ? 14 : isWide ? 24 : 20,
            paddingVertical: isNarrow ? 16 : 24,
          },
        ]}
      >
        <View style={styles.header}>
          <MaterialCommunityIcons
            name="chart-box-outline"
            size={20}
            color={colors.accentPrimary}
            style={styles.headerIcon}
          />
          <View style={styles.headerText}>
            <Text style={[styles.title, { color: colors.textPrimary }]}>
              {translate("Class insights")}
            </Text>
            <Text style={[styles.subtitle, { color: colors.textMuted }]}>
              {translate("Where the class is, before you decide what to reteach.")}
            </Text>
          </View>
          {onClose ? (
            <ActionButton
              colors={colors}
              label={translate("Close")}
              icon="close"
              variant="outline"
              accent={colors.textSecondary}
              onPress={onClose}
            />
          ) : null}
        </View>

        <View style={styles.strip}>
          <Stat
            colors={colors}
            icon="play-circle-outline"
            accent={colors.info}
            value={roster > started ? `${started}/${roster}` : String(started)}
            caption={translate("started")}
          />
          <View style={[styles.divider, { backgroundColor: colors.border }]} />
          <Stat
            colors={colors}
            icon="check-circle-outline"
            accent={colors.success}
            value={String(completed)}
            caption={translate("completed")}
          />
          <View style={[styles.divider, { backgroundColor: colors.border }]} />
          <Stat
            colors={colors}
            icon="pencil-outline"
            accent={colors.accentPrimary}
            value={String(answeredStudents)}
            caption={translate("answered at least one check")}
          />
        </View>

        {showTeachSense ? (
          <TeachSenseTabs active={activeTab} onChange={changeTab} colors={colors} t={translate} />
        ) : null}

        {children}
      </View>
    </View>
  );

  // TeachSense carries its own loading, failed and empty states and its own
  // ids, so it renders above the class-insights fallback rather than behind it
  // — one failed request should not take the other view down with it.
  if (activeTab === TEACHSENSE_TAB) {
    return page(
      <TeachSenseView
        teachsense={injected ? teachsense : loadedTeachSense.data}
        loading={injected ? teachsenseLoading : loadedTeachSense.loading}
        error={injected ? teachsenseError : loadedTeachSense.error}
        onRetry={onRetryTeachSense || loadedTeachSense.reload}
        focusConcept={focus?.key || null}
        focusLabel={focus?.label || ""}
        onClearFocus={() => setFocus(null)}
        colors={colors}
        isDarkMode={isDarkMode}
        isNarrow={isNarrow}
        t={translate}
      />
    );
  }

  // Loading, failed and untouched all render the same chrome around one line
  // of explanation, so the panel never vanishes out from under the teacher.
  const fallback = loading
    ? { text: "Loading class insights…" }
    : error
      ? { icon: "alert-outline", tone: colors.danger, text: "Class insights could not be loaded." }
      : !insights || (started === 0 && totalAnswers === 0)
        ? { icon: "account-clock-outline", text: "Nobody has started this lesson yet." }
        : null;

  if (fallback) {
    return page(
      <View style={[styles.stateCard, { backgroundColor: colors.panelBg, borderColor: colors.border }]}>
        {fallback.icon ? (
          <MaterialCommunityIcons
            name={fallback.icon}
            size={18}
            color={fallback.tone || colors.textMuted}
          />
        ) : (
          <ActivityIndicator size="small" color={colors.accentPrimary} />
        )}
        <Text style={[styles.stateText, { color: colors.textSecondary }]}>
          {translate(fallback.text)}
        </Text>
        {error && onRetry ? (
          <ActionButton
            colors={colors}
            label={translate("Retry")}
            icon="refresh"
            variant="outline"
            accent={colors.info}
            onPress={onRetry}
          />
        ) : null}
      </View>
    );
  }

  const ordered = orderConcepts(concepts);
  const verdict = buildVerdict({
    concepts,
    needsSupport,
    readyToAdvance,
    started,
    colors,
    t: translate,
  });

  const supportList = (
    <StudentList
      title="Needs support"
      icon="lifebuoy"
      entries={needsSupport}
      emptyText="No one is flagged for support right now."
      accent={colors.danger}
      hex={SUPPORT_HEX}
      showWeak
      cardStyle={cardStyle}
      colors={colors}
      isDarkMode={isDarkMode}
      t={translate}
    />
  );

  // Ready-to-advance rows carry no weak-concept pills: those are not why the
  // student is on this list, and they double the column height.
  const readyList = (
    <StudentList
      title="Ready to advance"
      icon="rocket-launch-outline"
      entries={readyToAdvance}
      emptyText="Nobody has cleared the mastery bar yet."
      accent={colors.success}
      hex={READY_HEX}
      showWeak={false}
      cardStyle={cardStyle}
      colors={colors}
      isDarkMode={isDarkMode}
      t={translate}
    />
  );

  return page(
    <>
      <VerdictCard verdict={verdict} colors={colors} isDarkMode={isDarkMode} />

      <View style={cardStyle}>
        <Text
          accessibilityRole="header"
          style={[styles.sectionTitle, { color: colors.textSecondary }]}
        >
          {translate("Concept mastery · weakest first")}
        </Text>
        {ordered.length === 0 ? (
          <Text style={[styles.emptyText, { color: colors.textMuted }]}>
            {translate("This lesson has no tagged concepts yet.")}
          </Text>
        ) : (
          ordered.map((concept, index) => (
            <ConceptRow
              key={concept.concept_key}
              concept={concept}
              colors={colors}
              isDarkMode={isDarkMode}
              isWide={isWide}
              isNarrow={isNarrow}
              divided={index > 0}
              onReadAnswers={openWrittenAnswers}
              t={translate}
            />
          ))
        )}
      </View>

      {isWide ? (
        <View testID="insights-student-columns" style={styles.studentCols}>
          <View style={styles.supportCol}>{supportList}</View>
          <View style={styles.readyCol}>{readyList}</View>
        </View>
      ) : (
        <>
          {supportList}
          {readyList}
        </>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  root: { width: "100%", alignItems: "center" },
  inner: { width: "100%", maxWidth: MAX_CONTENT_WIDTH },

  header: { flexDirection: "row", alignItems: "flex-start" },
  headerIcon: { marginTop: 3 },
  headerText: { flex: 1, minWidth: 0, marginLeft: 10, marginRight: 12 },
  title: { fontSize: 20, fontWeight: "800", lineHeight: 26 },
  subtitle: { fontSize: 13, fontWeight: "500", lineHeight: 18, marginTop: 3, maxWidth: 560 },

  // alignSelf keeps the strip its natural width; stretching it is what turned
  // three numbers into three 660px boxes in the version this replaces.
  strip: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: 6,
    marginTop: 14,
    marginBottom: 18,
    alignSelf: "flex-start",
  },
  stat: { flexDirection: "row", alignItems: "center" },
  statValue: {
    fontSize: 13,
    fontWeight: "800",
    lineHeight: 18,
    marginLeft: 6,
    fontVariant: ["tabular-nums"],
  },
  statCaption: { fontSize: 12, fontWeight: "500", lineHeight: 18, marginLeft: 5 },
  divider: { width: 1, height: 12, marginHorizontal: 12 },

  card: { borderRadius: 14, borderWidth: 1, padding: 20, marginBottom: 16 },
  cardNarrow: { padding: 14 },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "800",
    lineHeight: 16,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 12,
  },

  verdict: {
    borderRadius: 14,
    borderWidth: 1,
    borderLeftWidth: 3,
    padding: 16,
    marginBottom: 16,
  },
  verdictLabel: {
    fontSize: 11,
    fontWeight: "800",
    lineHeight: 14,
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  // Caps the sentence at a readable measure instead of letting it run the full
  // 1160px card width.
  verdictBody: { fontSize: 15, fontWeight: "600", lineHeight: 22, marginTop: 6, maxWidth: 720 },

  conceptRow: { flexDirection: "column", alignItems: "stretch", paddingVertical: 12 },
  conceptRowWide: { flexDirection: "row", alignItems: "center", minHeight: 56 },
  colLabel: { flex: 1, minWidth: 180, paddingRight: 16 },
  colBar: { width: BAR_COL, paddingRight: 12, justifyContent: "center" },
  colPct: { width: 56, paddingRight: 16 },
  colEvidence: { width: 210, paddingRight: 12 },
  colStatus: { width: 120, alignItems: "flex-start" },

  conceptTop: { flexDirection: "row", alignItems: "flex-end", marginBottom: 6 },
  conceptLabel: { flex: 1, fontSize: 14, fontWeight: "600", lineHeight: 19 },
  conceptPct: { fontSize: 17, fontWeight: "800", lineHeight: 22, fontVariant: ["tabular-nums"] },
  conceptUnanswered: { fontSize: 11, fontWeight: "700", lineHeight: 15 },
  alignRight: { textAlign: "right" },
  pctInline: { marginLeft: 8 },

  track: {
    height: 10,
    borderRadius: 999,
    borderWidth: 1,
    overflow: "hidden",
    width: "100%",
    maxWidth: TRACK_MAX,
    justifyContent: "center",
  },
  trackEmpty: { borderStyle: "dashed", opacity: 0.7 },
  interval: { position: "absolute", top: 0, bottom: 0 },
  fill: { position: "absolute", left: 0, top: 0, bottom: 0, borderRadius: 999 },
  marker: { position: "absolute", top: 0, bottom: 0, width: 2, marginLeft: -1 },

  conceptMeta: { flexDirection: "row", alignItems: "flex-start", gap: 12, marginTop: 8 },
  conceptMetaNarrow: { flexDirection: "column", alignItems: "flex-start", gap: 8, marginTop: 8 },
  evidenceCol: { flex: 1, minWidth: 0 },
  evidence: { fontSize: 11, fontWeight: "500", lineHeight: 15 },
  evidenceStacked: { marginTop: 2 },
  // alignSelf keeps the tap target the width of the sentence rather than the
  // whole column; flexShrink on the label lets it wrap instead of shoving the
  // chevron out of a 210px cell.
  evidenceLink: { flexDirection: "row", alignItems: "center", alignSelf: "flex-start", gap: 2 },
  evidenceLinkText: { flexShrink: 1, textDecorationLine: "underline" },

  studentCols: { flexDirection: "row", gap: 16 },
  supportCol: { flex: 1.6, minWidth: 0 },
  readyCol: { flex: 1, minWidth: 0 },

  listHeader: { flexDirection: "row", alignItems: "center", marginBottom: 10 },
  listTitle: { fontSize: 15, fontWeight: "800", lineHeight: 20, marginLeft: 8, flex: 1 },
  listCount: {
    borderRadius: 999,
    minWidth: 24,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignItems: "center",
  },
  listCountText: { fontSize: 12, fontWeight: "800" },

  studentRow: { flexDirection: "row", paddingVertical: 10, borderTopWidth: 1 },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    marginTop: 1,
  },
  avatarText: { fontSize: 11, fontWeight: "800" },
  studentBody: { flex: 1, minWidth: 0 },
  studentHead: { flexDirection: "row", alignItems: "baseline" },
  studentName: { flex: 1, fontSize: 14, fontWeight: "600", lineHeight: 19 },
  studentScore: {
    fontSize: 15,
    fontWeight: "800",
    lineHeight: 19,
    marginLeft: 8,
    fontVariant: ["tabular-nums"],
  },
  studentSample: { fontSize: 11, fontWeight: "500", lineHeight: 15, marginTop: 2 },
  weakRow: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 6 },
  moreText: { fontSize: 12, fontStyle: "italic", paddingTop: 10 },

  emptyText: { fontSize: 13, lineHeight: 19, fontStyle: "italic" },
  stateCard: {
    borderRadius: 14,
    borderWidth: 1,
    padding: 20,
    minHeight: 96,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  stateText: { fontSize: 13, lineHeight: 19, flex: 1 },
});

export default LessonInsightsPanel;
